import React, { useEffect } from 'react';
import { Typography, Checkbox } from '@scale/scaleui';
import { withErrorBoundary } from 'react-scale/containers/global/errorBoundary';
import type { ErrorPlaceholderProps } from 'react-scale/containers/global/errorBoundary';
import { useErrorBoundaryStore } from './hooks/useErrorBoundaryStore';
import { useSkipAuditAttemptsStore } from 'components/audits/ChatBulkAudit/hooks/useSkipAuditAttemptStore';

/**
 * Shared error boundary fallback component for ChatBulkAudit renderers
 */
function ChatBulkAuditErrorFallbackComponent(props: ErrorPlaceholderProps) {
  const { onReload, errorMessage } = props;
  const resetBoundary = useErrorBoundaryStore(state => state.resetBoundary);
  const setResetBoundary = useErrorBoundaryStore(state => state.setResetBoundary);
  if (resetBoundary === null) {
    setResetBoundary(onReload);
  }
  const lastRenderedId = useErrorBoundaryStore(state => state.lastRenderedId);
  const skipAuditAttemptIds = useSkipAuditAttemptsStore(state => state.skipAuditAttemptIds);
  const addSkipAuditAttemptId = useSkipAuditAttemptsStore(state => state.addSkipAuditAttemptId);

  useEffect(() => {
    addSkipAuditAttemptId(lastRenderedId);
  }, [lastRenderedId, addSkipAuditAttemptId]);

  return (
    <div className="m-4">
      <Typography.Paragraph2>
        This task appears to be corrupted. Want to skip this task?
      </Typography.Paragraph2>
      <Checkbox
        checked={skipAuditAttemptIds.has(lastRenderedId)}
        label="Skip task (due to task corruption)"
        disabled={true}
        variant="primary"
      />
      <div className="mt-3">
        <Typography.Paragraph2 className="text-danger-800 font-semibold">
          Error: {errorMessage || 'Unknown error'}
        </Typography.Paragraph2>
      </div>
    </div>
  );
}

export function useErrorBoundarySetup(auditAttemptId?: string) {
  const setLastRenderedId = useErrorBoundaryStore(state => state.setLastRenderedId);
  if (auditAttemptId) {
    setLastRenderedId(auditAttemptId);
  }
}

export function withChatBulkAuditErrorBoundary<T extends Record<string, any>>(
  Component: React.ComponentType<T>,
) {
  return withErrorBoundary({
    reloadable: true,
    ErrorPlaceholderComponent: ChatBulkAuditErrorFallbackComponent,
  })(Component);
}
